/**
 * KnowledgeLayout — Phase 1.3.3 + 2.1.2
 * App layout wrapper using the real TopNav from @amzn/neura-web-shared-components.
 *
 * SideNav items (role-based):
 *   Browse Articles     — all roles
 *   Create Article      — Champion, BarRaiser, Admin
 *   Validation Queue    — Champion, BarRaiser, Admin
 *   My Dashboard        — all roles
 *   Launch Milestones   — all roles
 *   Knowledge Gaps      — BarRaiser, Champion
 *   Admin               — Admin only
 */

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppLayout from '@cloudscape-design/components/app-layout';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Flashbar from '@cloudscape-design/components/flashbar';
import SideNavigation, { SideNavigationProps } from '@cloudscape-design/components/side-navigation';
import {
  TopNav,
  useAuth,
  useTheme,
  useNotifications,
} from '@amzn/neura-web-shared-components';
import type { User } from '@amzn/neura-web-shared-components';
import { Permission, UserRole } from '@amzn/neura-knowledge-management-model';
import { usePermissions } from '../hooks/usePermissions';
import { useBreadcrumbs } from '../hooks/useBreadcrumbs';

interface KnowledgeLayoutProps {
  children: React.ReactNode;
  /** Pass the article title when on an article view/edit page for accurate breadcrumbs */
  articleTitle?: string;
}

interface PermissionsApi {
  hasPermission: (permission: Permission) => boolean;
  isAtLeastRole: (minimumRole: UserRole) => boolean;
}

// Items not yet built — shown as greyed-out "Coming Soon" (non-navigable)
const COMING_SOON = (text: string): SideNavigationProps.Item => ({
  type: 'link',
  text: `${text}`,
  href: '#coming-soon',
  info: <span style={{ fontSize: '11px', color: 'var(--color-text-status-inactive, #8d99a8)', fontStyle: 'italic', marginLeft: 4 }}>WIP</span>,
});

function buildNavItems(perms: PermissionsApi): SideNavigationProps.Item[] {
  const items: SideNavigationProps.Item[] = [
    { type: 'link', text: 'Browse Articles', href: '/knowledge/browse' },
    COMING_SOON('My Dashboard'),
    COMING_SOON('Launch Milestones'),
  ];

  // Create Article — Champion+ (article:create permission)
  if (perms.hasPermission(Permission.ARTICLE_CREATE)) {
    items.splice(1, 0, { type: 'link', text: 'Create Article', href: '/knowledge/create' });
  }

  // Validation Queue — BarRaiser+ (article:validate permission)
  if (perms.hasPermission(Permission.ARTICLE_VALIDATE)) {
    items.push({ type: 'link', text: 'Validation Queue', href: '/knowledge/validation' });
  }

  // Knowledge Gaps — Champion+ (coming soon)
  if (perms.isAtLeastRole(UserRole.CHAMPION)) {
    items.push(COMING_SOON('Knowledge Gaps'));
  }

  // Admin section — BarRaiser+ sees some items, Admin sees all
  const adminItems: SideNavigationProps.Item[] = [];

  if (perms.isAtLeastRole(UserRole.BAR_RAISER)) {
    adminItems.push({ type: 'link', text: 'Bar Raiser Admin', href: '/admin/bar-raiser' });
    adminItems.push(COMING_SOON('Quality Dashboard'));
  }

  if (perms.isAtLeastRole(UserRole.ADMIN)) {
    adminItems.push({ type: 'link', text: 'Taxonomy Admin', href: '/admin/taxonomy' });
    adminItems.push(COMING_SOON('Slack Channels'));
    adminItems.push(COMING_SOON('Operations Monitoring'));
    adminItems.push(COMING_SOON('Coverage'));
  }

  if (adminItems.length > 0) {
    items.push({ type: 'divider' });
    items.push(...adminItems);
  }

  return items;
}

export default function KnowledgeLayout({ children, articleTitle }: KnowledgeLayoutProps) {
  const { user, signOut } = useAuth();
  const { toggleTheme } = useTheme();
  const { notifications, removeNotification } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(true);

  const { hasPermission, isAtLeastRole, role } = usePermissions();
  const navItems = buildNavItems({ hasPermission, isAtLeastRole });
  const breadcrumbs = useBreadcrumbs(articleTitle);

  // Map CognitoUser to the User shape expected by TopNav
  const topNavUser: User = {
    id: user?.sub ?? '',
    name: user?.displayName ?? user?.email ?? 'User',
    email: user?.email ?? '',
    role,
  };

  return (
    <>
      {/* Disable "coming soon" nav items via CSS */}
      <style>{`
        a[href="#coming-soon"] {
          opacity: 0.4 !important;
          pointer-events: none !important;
          cursor: default !important;
        }
      `}</style>
      <TopNav
        activeTab="knowledge"
        user={topNavUser}
        onNavigate={(tab) => {
          if (tab === 'chat') {
            window.location.href = 'https://neura.aws.dev/chat';
          } else {
            navigate('/');
          }
        }}
        onThemeToggle={toggleTheme}
        onLogout={signOut}
        helpUrl="https://docs.neura.aws.dev"
      />

      {/* Flashbar — renders notifications from useNotifications() */}
      {notifications.length > 0 && (
        <div style={{ position: 'fixed', top: '60px', right: '16px', zIndex: 1000, maxWidth: '420px' }}>
          <Flashbar
            items={notifications.map((n) => ({
              id: n.id,
              type: n.type,
              header: n.header,
              content: n.content,
              dismissible: n.dismissible !== false,
              onDismiss: () => removeNotification(n.id),
            }))}
          />
        </div>
      )}

      <AppLayout
        navigation={
          <SideNavigation
            activeHref={location.pathname}
            header={{ text: 'Knowledge', href: '/knowledge/browse' }}
            items={navItems}
            onFollow={(e) => {
              if (!e.detail.external) {
                e.preventDefault();
                if (e.detail.href !== '#' && e.detail.href !== '#coming-soon') {
                  navigate(e.detail.href);
                }
              }
            }}
          />
        }
        navigationOpen={navOpen}
        onNavigationChange={({ detail }) => setNavOpen(detail.open)}
        breadcrumbs={
          <BreadcrumbGroup
            items={breadcrumbs}
            onFollow={(e) => {
              e.preventDefault();
              navigate(e.detail.href);
            }}
            ariaLabel="Breadcrumbs"
          />
        }
        content={children}
        toolsHide
      />
    </>
  );
}
